import { Injectable, inject } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { tap, catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { SessionService } from '../modules/auth/services/session.service';

/**
 * Token Refresh Interceptor
 * 
 * Odpowiada za:
 * 1. Session rotation - odświeżanie tokenu gdy wygasa wkrótce
 * 2. Obsługa 401 błędów - automatyczne odświeżanie tokenu
 * 3. Queue requests - gdy refresh jest w toku, kolejkuje requesty
 * 
 * WAŻNE: Używa SessionService (nie AuthService!) aby uniknąć circular dependency.
 * AuthInterceptor dodaje Authorization header.
 */
@Injectable()
export class TokenRefreshInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
  private sessionCheckInProgress = false;
  
  private router = inject(Router);

  constructor(
    private sessionService: SessionService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Pomijaj refresh dla endpointów publicznych
    if (this.isPublicAuthEndpoint(request.url)) {
      return next.handle(request);
    }

    // Sprawdź czy token wygasa wkrótce - proactive refresh
    this.checkAndRotateTokenIfNeeded();

    return next.handle(request).pipe(
      tap((event) => {
        // Sprawdź czy backend zwrócił nowy token w response
        if (event instanceof HttpResponse) {
          this.extractAndUpdateTokenFromResponse(event);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        // 401 - Unauthorized - spróbuj refresh token
        if (error.status === 401 && !this.isTokenRefreshEndpoint(request.url)) {
          return this.handle401Error(request, next);
        }

        return throwError(() => error);
      })
    );
  }

  /**
   * Proactive refresh - sprawdź czy token wygasa wkrótce i odśwież z wyprzedzeniem
   */
  private checkAndRotateTokenIfNeeded(): void {
    if (this.sessionCheckInProgress || this.isRefreshing) {
      return;
    }

    // DUAL MODE:
    // Web: token expiring soon + HttpOnly Cookie istnieje (zakładamy że tak)
    // Mobile: token expiring soon + refresh token w SessionService
    const isMobile = this.isMobilePlatform();
    const hasRefreshToken = isMobile ? !!this.sessionService.getRefreshToken() : true; // Web: zakładamy cookie

    if (this.sessionService.isTokenExpiringSoon() && hasRefreshToken) {
      this.sessionCheckInProgress = true;
      console.log('[TokenRefresh] Proactive token refresh - token expiring soon', {
        platform: isMobile ? 'mobile' : 'web'
      });

      this.sessionService.refreshToken().subscribe({
        next: () => {
          console.log('[TokenRefresh] Proactive refresh successful');
          this.sessionCheckInProgress = false;
        },
        error: (error) => {
          console.error('[TokenRefresh] Proactive refresh failed:', error);
          this.sessionCheckInProgress = false;
        },
      });
    }
  }

  /**
   * Dodaj Authorization header do requestu
   */
  private addAuthorizationHeader(
    request: HttpRequest<any>,
    authHeader: string
  ): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: authHeader,
      },
    });
  }

  /**
   * Sprawdź czy response zawiera nowy token i zaktualizuj go
   * (Backend może zwrócić nowy token w dowolnym response)
   */
  private extractAndUpdateTokenFromResponse(response: HttpResponse<any>): void {
    // Sprawdź Authorization header
    const authHeader = response.headers.get('Authorization') || response.headers.get('authorization');
    if (authHeader && authHeader.toLowerCase().startsWith('bearer ')) {
      const newToken = authHeader.slice('Bearer '.length).trim();
      if (newToken && newToken.length > 0) {
        console.log('[TokenRefresh] New token received from Authorization header');
        // Token rotation handled by SessionService
      }
    }
  }

  /**
   * Obsługa 401 - Unauthorized
   * Kolejkuje requesty gdy refresh jest w toku
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      // ⚠️ DUAL MODE:
      // Web: refresh token jest w HttpOnly Cookie (nie mamy dostępu, ale przeglądarka wyśle)
      // Mobile: refresh token jest w SessionService
      const isMobile = this.isMobilePlatform();
      const refreshToken = this.sessionService.getRefreshToken();

      // Web: zawsze próbuj refresh (cookie się wyśle automatycznie)
      // Mobile: sprawdź czy mamy refresh token
      if (isMobile && !refreshToken) {
        console.log('[TokenRefresh] Mobile: No refresh token available');
        this.isRefreshing = false;
        this.handleLogout();
        return throwError(() => new Error('No refresh token available'));
      }

      console.log('[TokenRefresh] 401 received, attempting token refresh...', {
        platform: isMobile ? 'mobile' : 'web',
        hasRefreshToken: isMobile ? !!refreshToken : 'N/A (cookie)'
      });
      
      return this.sessionService.refreshToken().pipe(
        switchMap(() => {
          this.isRefreshing = false;
          const newToken = this.sessionService.getAccessToken();

          if (newToken) {
            console.log('[TokenRefresh] Refresh successful, retrying original request');
            this.refreshTokenSubject.next(newToken);
            return next.handle(this.addAuthorizationHeader(request, `Bearer ${newToken}`));
          } else {
            console.error('[TokenRefresh] No new token after refresh');
            this.handleLogout();
            return throwError(() => new Error('Failed to refresh token'));
          }
        }),
        catchError((error) => {
          console.error('[TokenRefresh] Refresh failed:', error);
          this.isRefreshing = false;
          
          // Tylko wyloguj przy błędach autentykacji (401, 403)
          // NIE wyloguj przy błędach serwera (5xx) lub sieci (0, brak internetu)
          const status = error?.status;
          const isAuthError = status === 401 || status === 403;
          const isServerError = status >= 500 && status < 600;
          const isNetworkError = status === 0 || !status;
          
          if (isAuthError) {
            console.log('[TokenRefresh] Auth error - logging out');
            this.handleLogout();
          } else if (isServerError || isNetworkError) {
            console.warn('[TokenRefresh] Server/network error - keeping session, user can retry');
            // NIE wylogowujemy - użytkownik może spróbować ponownie
          } else {
            console.log('[TokenRefresh] Unknown error - logging out');
            this.handleLogout();
          }
          
          return throwError(() => error);
        })
      );
    } else {
      // Refresh już w toku - czekaj na nowy token i ponów request
      console.log('[TokenRefresh] Refresh in progress, queuing request...');
      return this.refreshTokenSubject.pipe(
        filter(token => token != null),
        take(1),
        switchMap(token => {
          console.log('[TokenRefresh] Retrying queued request with new token');
          return next.handle(this.addAuthorizationHeader(request, `Bearer ${token}`));
        })
      );
    }
  }

  /**
   * Wyloguj użytkownika i przekieruj do logowania
   */
  private handleLogout(): void {
    this.sessionService.clearSession();
    this.router.navigate(['/auth/login']);
  }

  /**
   * Sprawdź czy to platforma mobile (dla dual-mode auth)
   */
  private isMobilePlatform(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /mobile|android|ios|iphone|ipad|ipod/.test(userAgent);
  }

  /**
   * Sprawdź czy to endpoint odświeżania tokenu
   */
  private isTokenRefreshEndpoint(url: string): boolean {
    return url.includes('/auth/refresh') || url.includes('/auth/login') || url.includes('/auth/verify-mfa');
  }

  /**
   * Sprawdź czy to publiczny endpoint autentykacji
   */
  private isPublicAuthEndpoint(url: string): boolean {
    const publicEndpoints = [
      '/auth/activate-account',
      '/auth/activate',
      '/auth/reset-password',
      '/auth/request-password-reset',
      '/auth/register',
      '/auth/guest-login',
      '/auth/resend-activation',
      '/auth/login',
      '/auth/refresh'  // ✅ NIE dodawaj Authorization header (używamy cookie!)
    ];
    return publicEndpoints.some(endpoint => url.includes(endpoint));
  }
}
